"use client";

import { useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Hash,
  ShieldCheck,
  Upload,
  Save,
  Loader2,
  X,
  Image as ImageIcon,
  FileText,
  Download,
  ExternalLink,
  BellRing,
  Plus,
  Users,
} from "lucide-react";

const decodeJWT = (token) => {
  try {
    const payload = token.split(".")[1];
    const decodedPayload = atob(payload);
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error("Erro ao decodificar JWT:", error);
    return null;
  }
};

const unwrapApiData = (res) => res?.data?.data ?? res?.data ?? null;

const extractErrorPayload = (err) =>
  err?.response?.data ?? { message: "Unexpected error" };

const parseErrorMap = (payload) => {
  if (!payload) return null;

  if (payload?.message && typeof payload.message === "object") {
    return payload.message;
  }

  if (payload?.data && typeof payload.data === "object") {
    return payload.data;
  }

  return null;
};

const getAuthContextFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  const decoded = decodeJWT(token);
  if (!decoded) return null;

  const userRoleId = Number(decoded.user_role_id ?? decoded.role_id ?? 0);

  const partnerCompanyId = Number(
    decoded.partner_company_id ??
      decoded.company_id ??
      decoded.partnerCompanyId ??
      decoded.companyId ??
      0
  );

  return {
    token,
    decoded,
    userRoleId: Number.isNaN(userRoleId) ? null : userRoleId,
    partnerCompanyId: Number.isNaN(partnerCompanyId) ? null : partnerCompanyId,
  };
};

const getApiBaseUrl = () => {
  const configured = (process.env.REACT_APP_API_PREFIX || "").replace(/\/+$/, "");
  return configured || "https://new.cajuu.app/api";
};

const getStorageBaseUrl = () => {
  const apiBase = getApiBaseUrl();
  return apiBase.replace(/\/api\/?$/, "").replace(/\/+$/, "");
};

const getAssetUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const value = String(pathOrUrl).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${getStorageBaseUrl()}/storage/${value.replace(/^\/+/, "")}`;
};

const normalizeObserverEmails = (value) => {
  let list = [];

  if (Array.isArray(value)) {
    list = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed) {
      list = [];
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        list = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        list = trimmed.split(/[,\n;]/);
      }
    }
  }

  const seen = new Set();

  return list
    .map((email) => String(email || "").trim().toLowerCase())
    .filter((email) => {
      if (!email || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
};

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

const normalizeCompany = (data) => ({
  id: data?.id ?? "",
  partner_company_name: data?.partner_company_name ?? "",
  phone: data?.phone ?? "",
  email: data?.email ?? "",
  address: data?.address ?? "",
  city: data?.city ?? "",
  state: data?.state ?? "",
  zipcode: data?.zipcode ?? "",
  dot: data?.dot ?? "",
  is_allowed_to_operate:
    data?.is_allowed_to_operate === null || data?.is_allowed_to_operate === undefined
      ? ""
      : String(data?.is_allowed_to_operate),
  payment_service_type: data?.payment_service_type ?? "",
  logo_path: data?.logo_path ?? "",
  logo_url: data?.logo_url ?? "",
  insurance_document_path: data?.insurance_document_path ?? "",
  insurance_document_url: data?.insurance_document_url ?? "",
  observer_emails: normalizeObserverEmails(
    data?.observer_emails ?? []
  ),
});

function FieldError({ error }) {
  if (!error) return null;
  const text = Array.isArray(error) ? error.join(" ") : String(error);
  return <div className="text-red-400 text-xs mt-2">{text}</div>;
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  Icon,
  error,
  type = "text",
  disabled = false,
  required = false,
  clearable = true,
}) {
  return (
    <div>
      <label className="text-sm font-medium text-white mb-2 block">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Icon className="text-gray-400" size={18} />
          </div>
        ) : null}

        <input
          type={type}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full ${
            Icon ? "pl-10" : "pl-3"
          } pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all ${
            disabled ? "opacity-70 cursor-not-allowed" : ""
          }`}
        />

        {clearable && !disabled && value ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onChange({ target: { value: "" } });
            }}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
            title="Clear"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <FieldError error={error} />
    </div>
  );
}

function UploadCard({
  title,
  description,
  accept,
  file,
  currentUrl,
  currentPath,
  onFileChange,
  onClearFile,
  error,
  type = "image",
}) {
  const previewUrl = useMemo(() => {
    if (!file) return "";
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const hasCurrent = Boolean(currentUrl || currentPath);
  const resolvedCurrentUrl = getAssetUrl(currentUrl || currentPath);
  const displayName = file?.name || (currentPath ? String(currentPath).split("/").pop() : "");

  return (
    <div className="bg-[#292826] border border-zinc-600 rounded-xl p-4 hover:border-amber-500/60 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <label className="text-sm font-medium text-white block">{title}</label>
          <p className="text-xs text-zinc-400 mt-1">{description}</p>
        </div>

        {hasCurrent && resolvedCurrentUrl ? (
          <a
            href={resolvedCurrentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-lg border border-zinc-600 text-zinc-200 hover:text-white hover:border-amber-500 transition-colors"
          >
            {type === "pdf" ? <Download size={15} /> : <ExternalLink size={15} />}
            {type === "pdf" ? "Download current" : "Open current"}
          </a>
        ) : null}
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <label className="flex flex-col items-center justify-center min-h-[140px] border border-dashed border-zinc-600 rounded-xl bg-[#1c1b19] hover:border-amber-500/70 cursor-pointer transition-colors p-5 text-center">
            <Upload size={28} className="text-amber-500 mb-3" />
            <span className="text-sm text-zinc-100 font-medium">
              {file ? file.name : "Click to select a file"}
            </span>
            <span className="text-xs text-zinc-500 mt-2">
              {type === "pdf" ? "Only PDF files are accepted." : "JPG, PNG, WEBP or GIF."}
            </span>

            <input
              type="file"
              accept={accept}
              onChange={onFileChange}
              className="hidden"
            />
          </label>

          {file ? (
            <button
              type="button"
              onClick={onClearFile}
              className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <X size={14} />
              Remove selected file
            </button>
          ) : null}

          {displayName ? (
            <p className="text-xs text-zinc-500 mt-3 break-all">
              Current file: {displayName}
            </p>
          ) : null}

          <FieldError error={error} />
        </div>

        <div className="w-full lg:w-48">
          <div className="h-[140px] bg-[#1c1b19] border border-zinc-700 rounded-xl flex items-center justify-center overflow-hidden">
            {type === "image" && (previewUrl || resolvedCurrentUrl) ? (
              <img
                src={previewUrl || resolvedCurrentUrl}
                alt="Company logo preview"
                className="w-full h-full object-contain p-3"
              />
            ) : type === "pdf" && (file || hasCurrent) ? (
              <div className="flex flex-col items-center justify-center text-zinc-200 text-center px-4">
                <FileText size={36} className="text-amber-500 mb-3" />
                <span className="text-xs break-all">
                  {file?.name || "Insurance document"}
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-zinc-500 text-center px-4">
                {type === "pdf" ? <FileText size={34} /> : <ImageIcon size={34} />}
                <span className="text-xs mt-2">
                  {type === "pdf" ? "No PDF selected" : "No logo selected"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ObserverEmailManager({
  emails,
  value,
  onValueChange,
  onAdd,
  onRemove,
  onKeyDown,
  error,
  inputError,
}) {
  return (
    <div className="relative overflow-hidden bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
      <div className="absolute -top-20 -right-20 h-44 w-44 rounded-full bg-amber-500/10 blur-3xl"></div>

      <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 shrink-0 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <BellRing className="text-amber-400" size={24} />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-lg font-bold text-white">
                Load Creation Observers
              </h3>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
                <Users size={14} />
                {emails.length} {emails.length === 1 ? "observer" : "observers"}
              </span>
            </div>
            <p className="text-sm text-zinc-400 mt-2 max-w-3xl">
              Add people who should be silently notified whenever a new Load
              Transport is created for this Partner Company. These emails are
              added to BCC together with Admins and Carrier Owners.
            </p>
          </div>
        </div>
      </div>

      <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
        <div>
          <label className="text-sm font-medium text-white mb-2 block">
            Observer email
          </label>

          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Mail className="text-gray-400" size={18} />
            </div>

            <input
              type="email"
              value={value}
              onChange={(event) => onValueChange(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="observer@email.com"
              className="w-full pl-10 pr-3 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            />
          </div>

          {inputError ? (
            <div className="text-red-400 text-xs mt-2">{inputError}</div>
          ) : null}
        </div>

        <div className="flex lg:items-end">
          <button
            type="button"
            onClick={onAdd}
            className="w-full lg:w-auto inline-flex items-center justify-center gap-2 text-sm px-5 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 transition-colors text-white font-semibold"
          >
            <Plus size={18} />
            Add observer
          </button>
        </div>
      </div>

      <div className="relative mt-5">
        {emails.length ? (
          <div className="flex flex-wrap gap-2">
            {emails.map((email) => (
              <span
                key={email}
                className="group inline-flex items-center gap-2 rounded-full border border-zinc-600 bg-[#292826] px-3 py-2 text-sm text-zinc-100 hover:border-amber-500/70 transition-colors"
              >
                <Mail size={14} className="text-amber-400" />
                <span className="break-all">{email}</span>
                <button
                  type="button"
                  onClick={() => onRemove(email)}
                  className="ml-1 rounded-full p-1 text-zinc-400 hover:text-white hover:bg-red-500/20 transition-colors"
                  title="Remove observer"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-zinc-700 bg-[#292826]/60 px-4 py-5 text-center">
            <p className="text-sm text-zinc-300 font-medium">
              No observers added yet.
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              Add one or more emails above to keep external managers,
              dispatchers or accounting contacts informed.
            </p>
          </div>
        )}
      </div>

      <FieldError error={error} />
    </div>
  );
}

function PageLoader() {
  return (
    <div className="bg-[#1c1b19] border border-zinc-700 rounded-xl p-10 mt-6">
      <div className="flex flex-col items-center justify-center gap-3 text-zinc-300">
        <Loader2 className="animate-spin" size={28} />
        <span className="text-sm">Loading company data...</span>
      </div>
    </div>
  );
}

function CompanyManagement() {
  const navigate = useNavigate();

  const getAxiosConfig = (extraConfig = {}) => {
    const userToken = localStorage.getItem("token");

    return {
      ...extraConfig,
      headers: {
        "Screen-Name": "/companymanagement",
        ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
        ...(extraConfig.headers || {}),
      },
    };
  };

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [form, setForm] = useState(normalizeCompany({}));
  const [errors, setErrors] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [insuranceDocumentFile, setInsuranceDocumentFile] = useState(null);
  const [observerEmailInput, setObserverEmailInput] = useState("");
  const [observerEmailError, setObserverEmailError] = useState("");

  const observerEmails = useMemo(
    () => normalizeObserverEmails(form.observer_emails),
    [form.observer_emails]
  );

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addObserverEmail = () => {
    const email = observerEmailInput.trim().toLowerCase();

    if (!email) {
      setObserverEmailError("Type an email before adding it.");
      return;
    }

    if (!isValidEmail(email)) {
      setObserverEmailError("Type a valid email address.");
      return;
    }

    if (observerEmails.includes(email)) {
      setObserverEmailError("This observer is already on the list.");
      return;
    }

    if (observerEmails.length >= 50) {
      setObserverEmailError("You can add up to 50 observers.");
      return;
    }

    setField("observer_emails", [...observerEmails, email]);
    setObserverEmailInput("");
    setObserverEmailError("");
  };

  const removeObserverEmail = (emailToRemove) => {
    setField(
      "observer_emails",
      observerEmails.filter((email) => email !== emailToRemove)
    );
    setObserverEmailError("");
  };

  const handleObserverEmailKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addObserverEmail();
    }
  };

  useEffect(() => {
    const boot = async () => {
      setLoading(true);

      try {
        const auth = getAuthContextFromToken();

        if (!auth?.token) {
          Swal.fire({
            icon: "error",
            title: "Unauthorized",
            text: "You need to login again.",
          });
          navigate("/login");
          return;
        }

        if (![1, 4].includes(Number(auth.userRoleId))) {
          Swal.fire({
            icon: "error",
            title: "Access denied",
            text: "Only Admin or Company Owner can access Company settings.",
          });
          navigate("/dashboard");
          return;
        }

        if (!auth.partnerCompanyId) {
          Swal.fire({
            icon: "error",
            title: "Company not found",
            text: "Could not identify your company from the current token.",
          });
          navigate("/dashboard");
          return;
        }

        setCompanyId(auth.partnerCompanyId);

        const res = await axios.get(
          `${getApiBaseUrl()}/partnercompany/${auth.partnerCompanyId}`,
          getAxiosConfig()
        );
        const data = unwrapApiData(res);

        if (!data) {
          throw new Error("Company data not found.");
        }

        setForm(normalizeCompany(data));
      } catch (err) {
        const payload = extractErrorPayload(err);
        Swal.fire({
          icon: "error",
          title: "Failed to load",
          text:
            payload?.message?.toString?.() ||
            payload?.data?.toString?.() ||
            err?.message ||
            "Could not load company data.",
        });
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, [navigate]);

  const validateClient = () => {
    const nextErrors = {};

    if (!String(form.partner_company_name || "").trim()) {
      nextErrors.partner_company_name = ["Company name is required."];
    }

    if (!String(form.email || "").trim()) {
      nextErrors.email = ["Email is required."];
    }

    if (!String(form.address || "").trim()) {
      nextErrors.address = ["Address is required."];
    }

    if (!String(form.city || "").trim()) {
      nextErrors.city = ["City is required."];
    }

    if (!String(form.state || "").trim()) {
      nextErrors.state = ["State is required."];
    }

    if (!String(form.zipcode || "").trim()) {
      nextErrors.zipcode = ["Zipcode is required."];
    }

    if (!String(form.dot || "").trim()) {
      nextErrors.dot = ["DOT is required."];
    }

    const invalidObserverEmails = observerEmails.filter(
      (email) => !isValidEmail(email)
    );

    if (invalidObserverEmails.length) {
      nextErrors.observer_emails = [
        `Invalid observer email(s): ${invalidObserverEmails.join(", ")}`,
      ];
    }

    setErrors(Object.keys(nextErrors).length ? nextErrors : null);
    return Object.keys(nextErrors).length === 0;
  };

  const appendIfPresent = (formData, key, value) => {
    formData.append(key, value === null || value === undefined ? "" : value);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors(null);

    if (!companyId) return;

    if (!validateClient()) {
      Swal.fire({
        icon: "error",
        title: "Validation error",
        text: "Please review the highlighted fields.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("_method", "PUT");
      appendIfPresent(formData, "partner_company_name", form.partner_company_name);
      appendIfPresent(formData, "phone", form.phone);
      appendIfPresent(formData, "email", form.email);
      appendIfPresent(formData, "address", form.address);
      appendIfPresent(formData, "city", form.city);
      appendIfPresent(formData, "state", form.state);
      appendIfPresent(formData, "zipcode", form.zipcode);
      appendIfPresent(formData, "dot", form.dot);
      appendIfPresent(formData, "is_allowed_to_operate", form.is_allowed_to_operate);
      appendIfPresent(formData, "payment_service_type", form.payment_service_type);
      appendIfPresent(formData, "logo_path", form.logo_path);
      appendIfPresent(formData, "insurance_document_path", form.insurance_document_path);
      appendIfPresent(
        formData,
        "observer_emails",
        JSON.stringify(observerEmails)
      );

      if (logoFile) {
        formData.append("logo_file", logoFile);
      }

      if (insuranceDocumentFile) {
        formData.append("insurance_document_file", insuranceDocumentFile);
      }

      const response = await axios.post(
        `${getApiBaseUrl()}/partnercompany/${companyId}`,
        formData,
        getAxiosConfig()
      );

      const updatedCompany = unwrapApiData(response);
      if (updatedCompany) {
        setForm(normalizeCompany(updatedCompany));
      }

      setLogoFile(null);
      setInsuranceDocumentFile(null);

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Company data updated successfully.",
      });
    } catch (err) {
      const payload = extractErrorPayload(err);
      const serverErrors = parseErrorMap(payload);
      if (serverErrors) setErrors(serverErrors);

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          payload?.message?.toString?.() ||
          payload?.data?.toString?.() ||
          "Unexpected error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <div className="flex items-center justify-between gap-3">
            <InfoSection
              title="COMPANY DATA"
              description="Edit the core information, branding and documents of your company"
            />
          </div>
        </GroupedContainer>

        {loading ? (
          <PageLoader />
        ) : (
          <form onSubmit={submit} className="space-y-8 mt-6">
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  COMPANY INFORMATION
                </h2>
                <div className="h-px w-full bg-zinc-700"></div>
              </div>

              <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-2">
                    <TextField
                      label="Partner Company Name"
                      value={form.partner_company_name}
                      onChange={(e) =>
                        setField("partner_company_name", e.target.value)
                      }
                      placeholder="Company name"
                      Icon={Building2}
                      error={errors?.partner_company_name}
                      disabled
                      required
                      clearable={false}
                    />
                  </div>

                  <div>
                    <TextField
                      label="Phone"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="+1 (000) 000-0000"
                      Icon={Phone}
                      error={errors?.phone}
                      required
                    />
                  </div>

                  <div>
                    <TextField
                      label="Email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="company@email.com"
                      Icon={Mail}
                      error={errors?.email}
                      type="email"
                      required
                    />
                  </div>

                  <div>
                    <TextField
                      label="Zipcode"
                      value={form.zipcode}
                      onChange={(e) => setField("zipcode", e.target.value)}
                      placeholder="Zipcode"
                      Icon={Hash}
                      error={errors?.zipcode}
                      required
                    />
                  </div>

                  <div>
                    <TextField
                      label="City"
                      value={form.city}
                      onChange={(e) => setField("city", e.target.value)}
                      placeholder="City"
                      Icon={MapPin}
                      error={errors?.city}
                      required
                    />
                  </div>

                  <div>
                    <TextField
                      label="State"
                      value={form.state}
                      onChange={(e) => setField("state", e.target.value)}
                      placeholder="State"
                      Icon={MapPin}
                      error={errors?.state}
                      required
                    />
                  </div>

                  <div>
                    <TextField
                      label="Address"
                      value={form.address}
                      onChange={(e) => setField("address", e.target.value)}
                      placeholder="Street address"
                      Icon={MapPin}
                      error={errors?.address}
                      required
                    />
                  </div>

                  <div>
                    <TextField
                      label="DOT"
                      value={form.dot}
                      onChange={(e) => setField("dot", e.target.value)}
                      placeholder="DOT"
                      Icon={Hash}
                      error={errors?.dot}
                      disabled
                      required
                      clearable={false}
                    />
                  </div>

                  <div>
                    <TextField
                      label="Allowed to Operate"
                      value={form.is_allowed_to_operate}
                      onChange={(e) =>
                        setField("is_allowed_to_operate", e.target.value)
                      }
                      placeholder="1 or 0"
                      Icon={ShieldCheck}
                      error={errors?.is_allowed_to_operate}
                      disabled
                      clearable={false}
                      required
                    />
                  </div>

                  <input
                    type="hidden"
                    value={form.payment_service_type || ""}
                    readOnly
                  />
                </div>

                {errors?.payment_service_type ? (
                  <div className="mt-4 text-xs text-red-400">
                    {Array.isArray(errors.payment_service_type)
                      ? errors.payment_service_type.join(" ")
                      : String(errors.payment_service_type)}
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  BRANDING AND PUBLIC DOCUMENTS
                </h2>
                <div className="h-px w-full bg-zinc-700"></div>
              </div>

              <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <UploadCard
                    title="Company Logo"
                    description="This logo will be shown in the public company profile and internal documents."
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    file={logoFile}
                    currentUrl={form.logo_url}
                    currentPath={form.logo_path}
                    type="image"
                    onFileChange={(event) => {
                      const selectedFile = event.target.files?.[0] || null;
                      setLogoFile(selectedFile);
                    }}
                    onClearFile={() => setLogoFile(null)}
                    error={errors?.logo_file || errors?.logo_path}
                  />

                  <UploadCard
                    title="Insurance Document"
                    description="Upload a PDF document so customers can download insurance information from the public profile."
                    accept="application/pdf"
                    file={insuranceDocumentFile}
                    currentUrl={form.insurance_document_url}
                    currentPath={form.insurance_document_path}
                    type="pdf"
                    onFileChange={(event) => {
                      const selectedFile = event.target.files?.[0] || null;
                      setInsuranceDocumentFile(selectedFile);
                    }}
                    onClearFile={() => setInsuranceDocumentFile(null)}
                    error={
                      errors?.insurance_document_file ||
                      errors?.insurance_document_path
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  LOAD CREATION OBSERVERS
                </h2>
                <div className="h-px w-full bg-zinc-700"></div>
              </div>

              <ObserverEmailManager
                emails={observerEmails}
                value={observerEmailInput}
                onValueChange={(value) => {
                  setObserverEmailInput(value);
                  setObserverEmailError("");
                }}
                onAdd={addObserverEmail}
                onRemove={removeObserverEmail}
                onKeyDown={handleObserverEmailKeyDown}
                inputError={observerEmailError}
                error={errors?.observer_emails}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg border border-zinc-700 hover:bg-zinc-900 transition-colors text-white font-medium"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 transition-colors text-white font-medium disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                {submitting ? "Saving..." : "Save Company Data"}
              </button>
            </div>
          </form>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default CompanyManagement;
